// Default + web implementation. Metro resolves iap-bridge.native.ts on
// iOS/Android instead — that file pulls in react-native-iap, which has
// no web build. This file's stubs satisfy TypeScript (TS doesn't follow
// Metro's per-platform extension resolution) and provide a clear
// runtime error if web ever tries to take the IAP path.

export type CheckoutTier = "oneoff" | "monthly" | "annual";

export interface IapBridgePurchaseResult {
  /**
   * iOS: signed JWS transaction string (StoreKit 2).
   * Android: opaque Play Billing purchaseToken.
   */
  receipt: string;
  productId: string;
  /** Cleanup handle — call after server verification to remove the transaction from the platform queue. */
  finish: () => Promise<void>;
}

export interface StoreProduct {
  productId: string;
  /** Localized price string from the store, e.g. "$14.99" or "€12,99". */
  localizedPrice: string;
}

export interface IapBridge {
  initialize: () => Promise<void>;
  purchase: (
    productId: string,
    tier: CheckoutTier,
  ) => Promise<IapBridgePurchaseResult>;
  /** Native-only "Restore Purchases" path. Returns receipts the server should re-verify. */
  restore: () => Promise<IapBridgePurchaseResult[]>;
  /**
   * Item 8: Returns localized prices for the given product IDs from the
   * native store. On web (Stripe), returns an empty array so the paywall
   * falls back to the server-side USD prices.
   */
  getStoreProducts: (skus: string[]) => Promise<StoreProduct[]>;
}

export const iapBridge: IapBridge = {
  async initialize(): Promise<void> {
    // No-op on web — the native bridge gets replaced by Metro on
    // iOS/Android. Hitting this on web means a CTA leaked through that
    // shouldn't have.
  },
  async purchase(): Promise<IapBridgePurchaseResult> {
    throw new Error(
      "IAP not available on web — checkout should have routed to Stripe",
    );
  },
  async restore(): Promise<IapBridgePurchaseResult[]> {
    throw new Error("Restore Purchases not available on web");
  },
  async getStoreProducts(): Promise<StoreProduct[]> {
    // Web uses Stripe which serves localized pricing via its own UI.
    return [];
  },
};
